/**
 * メーカー見積書 (manufacturer_quotes) の hooks (v3.1.8 / Phase C)。
 *
 * - 読込: deals/{dealId}/manufacturer_quotes 配下を Firestore 直接読み取り
 * - 編集: AI 抽出値の手動修正は Firestore 直書き (editor 以上のルール)
 * - アップロード / 発注確定 / 送金申請 / 削除は Callable 経由
 */
import type {
  ImportDocumentType,
  ManufacturerQuote,
  PurchaseOrder,
  PurchaseOrderImportDocument,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  arrayUnion,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';

import { auth, db, functions, storage } from '@/lib/firebase';

export interface ManufacturerQuoteRecord extends ManufacturerQuote {
  id: string;
}

// ─── 一覧 ───

export function useDealManufacturerQuotes(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['manufacturer_quotes', 'deal', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<ManufacturerQuoteRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'deals', dealId, 'manufacturer_quotes'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ManufacturerQuote),
      }));
    },
  });
}

// ─── 手動編集 (AI 抽出値の修正、割引額入力等) ───

export function useUpdateManufacturerQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      manufacturerQuoteId: string;
      invoiceNumber?: string | null;
      totalAmount?: number | null;
      discountAmount?: number;
      notes?: string | null;
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'manufacturer_quotes', input.manufacturerQuoteId);
      const update: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };
      if (input.invoiceNumber !== undefined) update.invoiceNumber = input.invoiceNumber;
      if (input.totalAmount !== undefined) update.totalAmount = input.totalAmount;
      if (input.discountAmount !== undefined) update.discountAmount = input.discountAmount;
      if (input.notes !== undefined) update.notes = input.notes;
      await updateDoc(ref, update);
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({
        queryKey: ['manufacturer_quotes', 'deal', vars.dealId],
      }),
  });
}

// ─── 削除 (誤アップロード時) ───

export function useDeleteManufacturerQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId: string; manufacturerQuoteId: string }) => {
      const ref = doc(db, 'deals', input.dealId, 'manufacturer_quotes', input.manufacturerQuoteId);
      await deleteDoc(ref);
      // Storage の PDF も削除したいが、storage.rules で delete 許可済みのため
      // 別途バッチ清掃。今は Firestore のみ削除でメタデータを失効させる。
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: ['manufacturer_quotes', 'deal', vars.dealId],
      });
      qc.invalidateQueries({ queryKey: ['manufacturer_quotes', 'all'] });
    },
  });
}

// ─── Storage URL 取得 (PDF プレビュー用) ───

export function usePdfDownloadUrl(storagePath: string | null | undefined) {
  return useQuery({
    queryKey: ['storage_url', storagePath],
    enabled: !!storagePath,
    staleTime: 1000 * 60 * 30, // 30 分
    queryFn: async (): Promise<string> => {
      if (!storagePath) return '';
      return getDownloadURL(storageRef(storage, storagePath));
    },
  });
}

// ─── Callable: uploadManufacturerQuote ───

const uploadFn = httpsCallable<
  {
    dealId: string;
    fileName: string;
    /** PDF の base64 (storagePath 指定時は不要) */
    contentBase64?: string | null;
    contentType: string;
    invoiceId?: string | null;
    /** 保存済み PDF (deals/{dealId}/ 配下) を読み取る場合のパス (2026-10+) */
    storagePath?: string | null;
  },
  {
    manufacturerQuoteId: string;
    pdfStoragePath: string;
    invoiceNumber: string | null;
    totalAmount: number | null;
    extractionError: string | null;
  }
>(functions, 'uploadManufacturerQuote');

/** 保存済み書類の種類をプロフォーマにする時に使う (useMoveReceivedDocument)。 */
export const uploadManufacturerQuoteFn = uploadFn;

export function useUploadManufacturerQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NonNullable<Parameters<typeof uploadFn>[0]>) => {
      const r = await uploadFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: ['manufacturer_quotes', 'deal', vars.dealId],
      });
      qc.invalidateQueries({ queryKey: ['manufacturer_quotes', 'all'] });
    },
  });
}

// ─── Callable: updateManufacturerQuoteInvoiceLink ───

const updateLinkFn = httpsCallable<
  { dealId: string; manufacturerQuoteId: string; invoiceId: string | null },
  { manufacturerQuoteId: string; invoiceId: string | null }
>(functions, 'updateManufacturerQuoteInvoiceLink');

export function useUpdateManufacturerQuoteInvoiceLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NonNullable<Parameters<typeof updateLinkFn>[0]>) => {
      const r = await updateLinkFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: ['manufacturer_quotes', 'deal', vars.dealId],
      });
      qc.invalidateQueries({
        queryKey: ['deals', vars.dealId, 'activities'],
      });
    },
  });
}

// ─── Callable: confirmPurchaseOrder ───

const confirmPoFn = httpsCallable<
  {
    dealId: string;
    manufacturerQuoteId: string;
    poId?: string | null;
    discountAmount?: number;
  },
  {
    poId: string;
    manufacturerQuoteId: string;
    manufacturerMailSubject: string;
    manufacturerMailBody: string;
  }
>(functions, 'confirmPurchaseOrder');

export function useConfirmPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NonNullable<Parameters<typeof confirmPoFn>[0]>) => {
      const r = await confirmPoFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: ['manufacturer_quotes', 'deal', vars.dealId],
      });
      qc.invalidateQueries({
        queryKey: ['purchase_orders', 'deal', vars.dealId],
      });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

// 送金依頼 (submitTransferRequest) は useTransferRequests.ts へ移設（v3.x / 2026-07）。

// ─── PO 関連 (再エクスポート風) — DealFulfillmentTab で manufacturerQuoteId を確認するため ───

export function getPoWithManufacturerQuote(
  pos: Array<PurchaseOrder & { id: string }> | undefined,
  manufacturerQuoteId: string,
): (PurchaseOrder & { id: string }) | undefined {
  return (pos ?? []).find((p) => p.manufacturerQuoteId === manufacturerQuoteId);
}

// ─── 全商談のメーカー見積 (発注一覧の書類状況表示用) ───
// collectionGroup read は firestore.rules の {path=**}/manufacturer_quotes で許可済み。

export interface ManufacturerQuoteWithDeal extends ManufacturerQuoteRecord {
  dealId: string;
}

export function useAllManufacturerQuotes(enabled = true) {
  return useQuery({
    queryKey: ['manufacturer_quotes', 'all'],
    enabled,
    queryFn: async (): Promise<ManufacturerQuoteWithDeal[]> => {
      const snap = await getDocs(collectionGroup(db, 'manufacturer_quotes'));
      return snap.docs.map((d) => ({
        id: d.id,
        dealId: d.ref.parent.parent?.id ?? '',
        ...(d.data() as ManufacturerQuote),
      }));
    },
  });
}

// ─── 発注 (PO) 配下の PDF を Storage へ直接アップロード ───
// storage.rules: deals/{dealId}/purchase_orders/** は editor 以上・20MB 以下・PDF/画像のみ書込可。

async function uploadPoPdf(storagePath: string, file: File): Promise<void> {
  const { uploadBytes, ref: makeRef } = await import('firebase/storage');
  await uploadBytes(makeRef(storage, storagePath), file, {
    contentType: file.type,
  });
}

function safeStorageName(name: string): string {
  return name.replace(/[^\w.-]+/g, '_').slice(0, 80);
}

function invalidatePurchaseOrders(qc: ReturnType<typeof useQueryClient>, dealId: string) {
  qc.invalidateQueries({ queryKey: ['purchase_orders', 'deal', dealId] });
  qc.invalidateQueries({ queryKey: ['purchase_orders', 'all'] });
}

// ─── 最終インボイス PDF (旧称: 正式発注書。PO に officialOrderPdfPath を書き込み) ───
// 直接 Storage アップロード + Firestore 更新で対応 (callable 不要)。差し替えも同じ hook。

export function useUploadOfficialOrderPdf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId: string; poId: string; file: File }) => {
      const { dealId, poId, file } = input;
      const path = `deals/${dealId}/purchase_orders/${Date.now()}-${safeStorageName(file.name)}`;
      await uploadPoPdf(path, file);
      const ref = doc(db, 'deals', dealId, 'purchase_orders', poId);
      await updateDoc(ref, {
        officialOrderPdfPath: path,
        officialOrderFileName: file.name,
        officialOrderUploadedAt: serverTimestamp(),
        officialOrderUploadedBy: auth.currentUser?.uid ?? null,
        updatedAt: serverTimestamp(),
      });
      return { path };
    },
    onSuccess: (_d, vars) => invalidatePurchaseOrders(qc, vars.dealId),
  });
}

/** 最終インボイスの登録を外す。PDF 本体は Storage に残す (メーカー見積の削除と同じ扱い)。 */
export function useClearOfficialOrderPdf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId: string; poId: string }) => {
      const ref = doc(db, 'deals', input.dealId, 'purchase_orders', input.poId);
      await updateDoc(ref, {
        officialOrderPdfPath: null,
        officialOrderFileName: null,
        officialOrderUploadedAt: null,
        officialOrderUploadedBy: null,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_d, vars) => invalidatePurchaseOrders(qc, vars.dealId),
  });
}

// ─── DHL 輸入関税・関連書類 (PO.importDocuments) ───

export function useUploadImportDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      poId: string;
      files: File[];
      type: ImportDocumentType;
    }) => {
      const { dealId, poId, files, type } = input;
      const uid = auth.currentUser?.uid ?? '';
      const entries: PurchaseOrderImportDocument[] = [];
      for (const file of files) {
        const id = crypto.randomUUID();
        const path = `deals/${dealId}/purchase_orders/${poId}/import/${Date.now()}-${safeStorageName(file.name)}`;
        await uploadPoPdf(path, file);
        entries.push({
          id,
          type,
          storagePath: path,
          fileName: file.name,
          uploadedAt: Timestamp.now(),
          uploadedBy: uid,
        });
      }
      const ref = doc(db, 'deals', dealId, 'purchase_orders', poId);
      await updateDoc(ref, {
        importDocuments: arrayUnion(...entries),
        updatedAt: serverTimestamp(),
      });
      return { count: entries.length };
    },
    onSuccess: (_d, vars) => invalidatePurchaseOrders(qc, vars.dealId),
  });
}

/** importDocuments の 1 件を種類変更 or 削除する (配列の読み書きをトランザクションで行う)。 */
async function rewriteImportDocuments(
  dealId: string,
  poId: string,
  fn: (docs: PurchaseOrderImportDocument[]) => PurchaseOrderImportDocument[],
) {
  const ref = doc(db, 'deals', dealId, 'purchase_orders', poId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = ((snap.data() as PurchaseOrder | undefined)?.importDocuments ??
      []) as PurchaseOrderImportDocument[];
    tx.update(ref, { importDocuments: fn(current), updatedAt: serverTimestamp() });
  });
}

export function useUpdateImportDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      poId: string;
      documentId: string;
      type: ImportDocumentType;
    }) => {
      await rewriteImportDocuments(input.dealId, input.poId, (docs) =>
        docs.map((d) => (d.id === input.documentId ? { ...d, type: input.type } : d)),
      );
    },
    onSuccess: (_d, vars) => invalidatePurchaseOrders(qc, vars.dealId),
  });
}

/** DHL 書類の登録を外す。PDF 本体は Storage に残す。 */
export function useRemoveImportDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId: string; poId: string; documentId: string }) => {
      await rewriteImportDocuments(input.dealId, input.poId, (docs) =>
        docs.filter((d) => d.id !== input.documentId),
      );
    },
    onSuccess: (_d, vars) => invalidatePurchaseOrders(qc, vars.dealId),
  });
}

// ─── v3.2.22: メーカー宛 業務通知 ───

interface ManufacturerNotifyInput {
  dealId: string;
  to: string;
  manufacturerQuoteId?: string | null;
  poId?: string | null;
}

const sendQuoteRequestFn = httpsCallable<ManufacturerNotifyInput, { ok: boolean; reason?: string }>(
  functions,
  'sendManufacturerQuoteRequest',
);

export function useSendManufacturerQuoteRequest() {
  return useMutation({
    mutationFn: async (input: ManufacturerNotifyInput) => {
      const r = await sendQuoteRequestFn(input);
      return r.data;
    },
  });
}

const sendOrderRequestFn = httpsCallable<ManufacturerNotifyInput, { ok: boolean; reason?: string }>(
  functions,
  'sendManufacturerOrderRequest',
);

export function useSendManufacturerOrderRequest() {
  return useMutation({
    mutationFn: async (input: ManufacturerNotifyInput) => {
      const r = await sendOrderRequestFn(input);
      return r.data;
    },
  });
}

// ─── メール送信モーダル用: prepare（プレフィル取得）/ send（編集値で送信）───
export type ManufacturerMailKind = 'quote_request' | 'order_request';

const prepareManufacturerMailFn = httpsCallable<
  {
    dealId: string;
    kind: ManufacturerMailKind;
    manufacturerQuoteId?: string | null;
    poId?: string | null;
  },
  {
    defaultTo: string[];
    defaultCc: string[];
    fromAccountId: string | null;
    subject: string;
    body: string;
  }
>(functions, 'prepareManufacturerMail');

export function usePrepareManufacturerMail() {
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      kind: ManufacturerMailKind;
      manufacturerQuoteId?: string | null;
      poId?: string | null;
    }) => {
      const r = await prepareManufacturerMailFn(input);
      return r.data;
    },
  });
}

const sendManufacturerMailFn = httpsCallable<
  {
    dealId: string;
    to: string[];
    cc?: string[];
    subject: string;
    body: string;
    fromAccountId?: string | null;
    poId?: string | null;
    kind?: ManufacturerMailKind;
  },
  { ok: boolean; reason?: string }
>(functions, 'sendManufacturerMail');

export function useSendManufacturerMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      to: string[];
      cc?: string[];
      subject: string;
      body: string;
      fromAccountId?: string | null;
      poId?: string | null;
      kind?: ManufacturerMailKind;
    }) => {
      const r = await sendManufacturerMailFn(input);
      return r.data;
    },
    onSuccess: (_data, vars) => {
      // 「メーカー見積済み」ラベルを反映するため PO を再取得
      qc.invalidateQueries({
        queryKey: ['purchase_orders', 'deal', vars.dealId],
      });
    },
  });
}
